import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  IconButton,
  Input,
  Loading,
  Modal,
  PageHead,
  Select
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { FloorMap, type FloorMapTable, type FloorTableStatus } from '@billiard/ui'
import { dateTime, dateInputToMs, msToDateInput } from '@/lib/format'
import {
  BOOKING_SOURCE_LABEL,
  BOOKING_STATUS_LABEL,
  FLOOR_MAP_KEY,
  parseFloorMap,
  type ActiveSession,
  type Booking,
  type BookingStatus,
  type GameType,
  type SettingRow,
  type Table
} from '@/lib/config'

/** Смещение часового пояса клуба в минутах → «UTC+3» / «UTC+5:30». */
function tzLabel(offsetMin: number): string {
  const abs = Math.abs(offsetMin)
  const hh = Math.floor(abs / 60)
  const mm = abs % 60
  return `UTC${offsetMin < 0 ? '−' : '+'}${hh}${mm ? `:${String(mm).padStart(2, '0')}` : ''}`
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Все статусы' },
  { value: 'confirmed', label: BOOKING_STATUS_LABEL.confirmed },
  { value: 'pending', label: BOOKING_STATUS_LABEL.pending },
  { value: 'active', label: BOOKING_STATUS_LABEL.active },
  { value: 'completed', label: BOOKING_STATUS_LABEL.completed },
  { value: 'cancelled', label: BOOKING_STATUS_LABEL.cancelled },
  { value: 'no_show', label: BOOKING_STATUS_LABEL.no_show }
]

const DAY_MS = 24 * 60 * 60 * 1000

export function Bookings(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Бронирования"
        title="Брони столов"
        lead="Онлайн-брони с публичного сайта и ручные брони. Подтверждение, перенос, отмена и отметка «не пришёл»."
      />
      <ClubGate>
        <BookingsView />
      </ClubGate>
    </>
  )
}

/** Переключатель «Сетка занятости» / «План зала» / «Список броней». */
function BookingsView(): React.JSX.Element {
  const [view, setView] = useState<'grid' | 'map' | 'list'>('grid')
  return (
    <>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button
          className={`tab${view === 'grid' ? ' is-active' : ''}`}
          onClick={() => setView('grid')}
        >
          Сетка занятости
        </button>
        <button
          className={`tab${view === 'map' ? ' is-active' : ''}`}
          onClick={() => setView('map')}
        >
          План зала
        </button>
        <button
          className={`tab${view === 'list' ? ' is-active' : ''}`}
          onClick={() => setView('list')}
        >
          Список броней
        </button>
      </div>
      {view === 'grid' && <ScheduleGrid />}
      {view === 'map' && <FloorOccupancyMap />}
      {view === 'list' && <BookingsTable />}
    </>
  )
}

const HOUR_MS = 60 * 60 * 1000
// Статусы броней, при которых слот считается занятым (отменённые/неявки — свободны).
const OCCUPYING = new Set<BookingStatus>(['pending', 'confirmed', 'active', 'completed'])

type CellState = 'free' | 'booked' | 'game'

const CELL_BG: Record<CellState, string> = {
  free: 'transparent',
  booked: 'rgba(201, 168, 76, 0.38)',
  game: 'rgba(45, 160, 90, 0.55)'
}

/** Данные занятости на день, общие для сетки и плана зала. */
interface OccupancyDay {
  offsetMin: number
  /** Полночь выбранной даты в поясе клуба (epoch-ms). */
  dayStart: number
  settingsData: SettingRow[] | null
  tableList: Table[]
  gameTypeList: GameType[]
  kindName: (code?: string | null) => string
  /** Брони без назначенного стола (видны только в «Списке броней»). */
  noTableCount: number
  startHour: number
  endHour: number
  cellFor: (tableId: string, h: number) => { state: CellState; title: string }
  loading: boolean
  error: string | null
  reload: () => void
}

/**
 * Занятость столов на день: строки/глифы окрашены по приоритету «в игре»
 * (активная сессия) → «бронь» → свободно. Время считается в часовом поясе клуба
 * (settings.bookingUtcOffsetMinutes; по умолчанию 180 = UTC+3, Минск).
 * Живое обновление сессий/броней раз в 30 с (сессии приезжают с терминала синком).
 */
function useOccupancyDay(date: string): OccupancyDay {
  const query = useConfigQuery()
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000)
    return () => clearInterval(t)
  }, [])

  const settings = useFetch<SettingRow[]>('/v1/settings', query, [query?.clubId])
  const offsetMin = useMemo(() => {
    const raw = (settings.data ?? []).find((s) => s.key === 'bookingUtcOffsetMinutes')?.value
    const n = Number(raw)
    return raw != null && raw !== '' && Number.isFinite(n) ? n : 180
  }, [settings.data])

  // Полночь выбранной даты В ЧАСОВОМ ПОЯСЕ КЛУБА, выраженная абсолютным epoch-ms.
  // Дальше все ячейки (dayStart + h·час) и метки часов (h%24) — это настенное время клуба.
  const dayStart = useMemo(() => {
    const [y, m, d] = date.split('-').map(Number)
    return Date.UTC(y, (m ?? 1) - 1, d ?? 1) - offsetMin * 60_000
  }, [date, offsetMin])
  const dayEnd = dayStart + DAY_MS

  const gridQuery = useMemo(
    () => ({ ...(query ?? {}), from: String(dayStart), to: String(dayEnd - 1) }),
    [query, dayStart, dayEnd]
  )

  const tables = useFetch<Table[]>('/v1/tables', query, [query?.clubId])
  const gameTypes = useFetch<GameType[]>('/v1/game-types', query, [query?.clubId])
  const bookings = useFetch<Booking[]>('/v1/bookings', gridQuery, [
    query?.clubId,
    date,
    tick,
    offsetMin
  ])
  const sessions = useFetch<ActiveSession[]>('/v1/bookings/active-sessions', query, [
    query?.clubId,
    tick
  ])

  const tableList = (tables.data ?? []).slice().sort((a, b) => (a.name > b.name ? 1 : -1))
  const gameTypeList = gameTypes.data ?? []
  const kindName = (code?: string | null): string =>
    code ? (gameTypeList.find((g) => g.code === code)?.name ?? code) : ''

  const now = Date.now()

  // Занятые бронями интервалы (только со столом и «занимающим» статусом).
  const bookingIntervals = (bookings.data ?? []).filter((b) => b.tableId && OCCUPYING.has(b.status))
  // Интервалы игр: от старта до планового конца или «сейчас» (что позже).
  const sessionIntervals = (sessions.data ?? [])
    .filter((s) => s.tableId)
    .map((s) => ({
      ...s,
      end: Math.max(now, s.startedAt + (s.plannedMinutes ?? 0) * 60_000)
    }))

  const noTableCount = (bookings.data ?? []).filter(
    (b) => !b.tableId && OCCUPYING.has(b.status)
  ).length

  // Диапазон часов: базово 10–24, расширяем под данные (в пределах 6–30).
  const { startHour, endHour } = useMemo(() => {
    let lo = 10
    let hi = 24
    const spans = [
      ...bookingIntervals.map((b) => [b.startsAt, b.endsAt] as const),
      ...sessionIntervals.map((s) => [s.startedAt, s.end] as const)
    ]
    for (const [s, e] of spans) {
      const sH = Math.floor((Math.max(s, dayStart) - dayStart) / HOUR_MS)
      const eH = Math.ceil((Math.min(e, dayEnd) - dayStart) / HOUR_MS)
      if (sH < lo) lo = sH
      if (eH > hi) hi = eH
    }
    return { startHour: Math.max(6, lo), endHour: Math.min(30, Math.max(hi, lo + 1)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings.data, sessions.data, dayStart, dayEnd])

  const cellFor = (tableId: string, h: number): { state: CellState; title: string } => {
    const cellStart = dayStart + h * HOUR_MS
    const cellEnd = cellStart + HOUR_MS
    const sess = sessionIntervals.find(
      (s) => s.tableId === tableId && s.startedAt < cellEnd && s.end > cellStart
    )
    if (sess) {
      const mins = Math.floor((now - sess.startedAt) / 60_000)
      return {
        state: 'game',
        title: `В игре${sess.customerName ? ` · ${sess.customerName}` : ''} · ${mins} мин`
      }
    }
    const bk = bookingIntervals.find(
      (b) => b.tableId === tableId && b.startsAt < cellEnd && b.endsAt > cellStart
    )
    if (bk) {
      return {
        state: 'booked',
        title: `Бронь · ${bk.customerName} · ${dateTime(bk.startsAt).split(', ')[1] ?? ''}–${
          dateTime(bk.endsAt).split(', ')[1] ?? ''
        }`
      }
    }
    return { state: 'free', title: '' }
  }

  return {
    offsetMin,
    dayStart,
    settingsData: settings.data,
    tableList,
    gameTypeList,
    kindName,
    noTableCount,
    startHour,
    endHour,
    cellFor,
    loading: tables.loading || bookings.loading,
    error: tables.error || bookings.error || sessions.error,
    reload: () => {
      bookings.reload()
      sessions.reload()
    }
  }
}

/** Сетка занятости на день: строки — столы, колонки — часы. */
function ScheduleGrid(): React.JSX.Element {
  const [date, setDate] = useState(() => msToDateInput(Date.now()))
  const {
    offsetMin,
    tableList,
    kindName,
    noTableCount,
    startHour,
    endHour,
    cellFor,
    loading,
    error,
    reload
  } = useOccupancyDay(date)

  const hours: number[] = []
  for (let h = startHour; h < endHour; h++) hours.push(h)

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ width: 'auto' }}
          />
          <Button variant="ghost" size="sm" onClick={reload}>
            Обновить
          </Button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            Часовой пояс клуба: {tzLabel(offsetMin)}
          </span>
        </div>
        <div className="toolbar__right">
          <div className="legend" style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
            <LegendDot bg={CELL_BG.booked} label="Бронь" />
            <LegendDot bg={CELL_BG.game} label="В игре" />
            <LegendDot bg="transparent" label="Свободно" bordered />
          </div>
        </div>
      </div>

      {loading && <Loading />}
      {error && <ErrorState message={error} />}
      {!loading && !error && tableList.length === 0 && (
        <EmptyState message="Столы ещё не заданы — добавьте их в разделе «Столы»." />
      )}

      {!loading && !error && tableList.length > 0 && (
        <>
          {noTableCount > 0 && (
            <div className="muted" style={{ margin: '4px 0 12px', fontSize: 13 }}>
              {noTableCount} бронь(и) без назначенного стола — их видно во вкладке «Список броней».
            </div>
          )}
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="rtable schedule-grid" style={{ minWidth: 'max-content' }}>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, minWidth: 140 }}>Стол</th>
                  {hours.map((h) => (
                    <th
                      key={h}
                      className="num"
                      style={{ minWidth: 44, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {String(h % 24).padStart(2, '0')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableList.map((t) => (
                  <tr key={t.id}>
                    <td
                      style={{
                        position: 'sticky',
                        left: 0,
                        background: 'var(--surface, #fff)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {t.name}
                      {t.kind ? <span className="muted"> · {kindName(t.kind)}</span> : ''}
                    </td>
                    {hours.map((h) => {
                      const { state, title } = cellFor(t.id, h)
                      return (
                        <td
                          key={h}
                          title={title}
                          style={{
                            background: CELL_BG[state],
                            borderLeft: '1px solid var(--hairline-2, #eee)',
                            height: 30,
                            cursor: state === 'free' ? 'default' : 'help'
                          }}
                        />
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

/**
 * План зала с занятостью: те же данные, что у сетки, но статус на выбранный час
 * рисуется на миникарте (раскладка — settings.floorMap из «Столы» → «План зала»).
 */
function FloorOccupancyMap(): React.JSX.Element {
  const [date, setDate] = useState(() => msToDateInput(Date.now()))
  const occ = useOccupancyDay(date)
  const [hour, setHour] = useState<number | null>(null) // null → «сейчас»/час открытия

  const layout = useMemo(
    () => parseFloorMap(occ.settingsData?.find((s) => s.key === FLOOR_MAP_KEY)?.value),
    [occ.settingsData]
  )

  const hours: number[] = []
  for (let h = occ.startHour; h < occ.endHour; h++) hours.push(h)

  // Час по умолчанию: для «сегодня» — текущий час клуба, иначе час открытия.
  const nowHour = Math.floor((Date.now() - occ.dayStart) / HOUR_MS)
  const defaultHour = Math.min(Math.max(nowHour, occ.startHour), occ.endHour - 1)
  const activeHour = hour ?? (nowHour >= 0 && nowHour < 24 ? defaultHour : occ.startHour)

  const placed: FloorMapTable[] = useMemo(() => {
    if (!layout) return []
    const out: FloorMapTable[] = []
    for (const t of occ.tableList) {
      const e = layout.tables[t.id]
      if (!e) continue
      out.push({
        id: t.id,
        name: t.name,
        kind: t.kind ?? null,
        x: e.x,
        y: e.y,
        rot: e.rot,
        scale: e.scale
      })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, occ.tableList])
  const offPlan = occ.tableList.filter((t) => !layout?.tables[t.id])

  const statusFor = (tableId: string): FloorTableStatus => {
    const { state } = occ.cellFor(tableId, activeHour)
    return state === 'booked' ? 'busy' : state
  }
  const statuses: Record<string, FloorTableStatus> = {}
  for (const t of occ.tableList) statuses[t.id] = statusFor(t.id)

  if (occ.error) return <ErrorState message={occ.error} />
  if (occ.loading || occ.settingsData == null) return <Loading />

  if (!layout || placed.length === 0) {
    return (
      <>
        <EmptyState message="План зала не настроен." />
        <p style={{ marginTop: 12 }}>
          <Link className="btn btn--sm" to="/tables">
            Настроить в «Столы» → «План зала»
          </Link>
        </p>
      </>
    )
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value)
              setHour(null)
            }}
            style={{ width: 'auto' }}
          />
          <Button variant="ghost" size="sm" onClick={occ.reload}>
            Обновить
          </Button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            Часовой пояс клуба: {tzLabel(occ.offsetMin)}
          </span>
        </div>
        <div className="toolbar__right">
          <div className="legend" style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
            <LegendDot bg={CELL_BG.booked} label="Бронь" />
            <LegendDot bg={CELL_BG.game} label="В игре" />
            <LegendDot bg="transparent" label="Свободно" bordered />
          </div>
        </div>
      </div>

      <div className="hour-chips">
        {hours.map((h) => (
          <button
            key={h}
            className={`btn btn--sm${activeHour === h ? ' btn--primary' : ''}`}
            onClick={() => setHour(h)}
          >
            {String(h % 24).padStart(2, '0')}:00
          </button>
        ))}
      </div>

      <FloorMap
        className="floor-occupancy"
        fitToContent
        layout={{ canvas: layout.canvas, tables: placed }}
        gameTypes={occ.gameTypeList.map((g) => ({
          code: g.code,
          name: g.name,
          color: g.color ?? null
        }))}
        statuses={statuses}
      />

      {offPlan.length > 0 && (
        <div className="floor-tray">
          <div className="floor-tray__title">Вне плана</div>
          <div className="floor-tray__chips">
            {offPlan.map((t) => {
              const st = statuses[t.id]
              const bg =
                st === 'game' ? CELL_BG.game : st === 'busy' ? CELL_BG.booked : 'transparent'
              return (
                <span
                  key={t.id}
                  className="btn btn--sm"
                  style={{ background: bg, cursor: 'default' }}
                >
                  {t.name}
                  {t.kind ? ` · ${occ.kindName(t.kind)}` : ''}
                </span>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

function LegendDot({
  bg,
  label,
  bordered
}: {
  bg: string
  label: string
  bordered?: boolean
}): React.JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          background: bg,
          border: bordered ? '1px solid var(--hairline, #ccc)' : 'none',
          display: 'inline-block'
        }}
      />
      {label}
    </span>
  )
}

function BookingsTable(): React.JSX.Element {
  const query = useConfigQuery()
  const [from, setFrom] = useState(() => msToDateInput(Date.now()))
  const [to, setTo] = useState(() => msToDateInput(Date.now() + 14 * DAY_MS))
  const [status, setStatus] = useState('')

  const listQuery = useMemo(
    () => ({
      ...(query ?? {}),
      from: String(dateInputToMs(from)),
      to: String(dateInputToMs(to) + DAY_MS - 1),
      ...(status ? { status } : {})
    }),
    [query, from, to, status]
  )

  const list = useFetch<Booking[]>('/v1/bookings', listQuery, [query?.clubId, from, to, status])
  const tables = useFetch<Table[]>('/v1/tables', query, [query?.clubId])
  const gameTypes = useFetch<GameType[]>('/v1/game-types', query, [query?.clubId])
  const [editing, setEditing] = useState<'new' | null>(null)
  const [deleting, setDeleting] = useState<Booking | null>(null)
  const statusMut = useMutate()
  const del = useMutate()

  const rows = list.data ?? []
  const tableList = tables.data ?? []
  const gameTypeList = gameTypes.data ?? []
  const tableName = (id?: string | null): string =>
    tableList.find((t) => t.id === id)?.name ?? (id ? '—' : 'любой')
  const kindName = (code?: string | null): string =>
    code ? (gameTypeList.find((g) => g.code === code)?.name ?? code) : '—'

  const changeStatus = async (b: Booking, next: BookingStatus): Promise<void> => {
    const ok = await statusMut.mutate(() =>
      api.post(`/v1/bookings/${b.id}/status`, { status: next }, { query })
    )
    if (ok) list.reload()
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/bookings/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ width: 'auto' }}
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={{ width: 'auto' }}
          />
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ width: 'auto' }}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новая бронь
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Броней на выбранный период нет."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Клиент</th>
                <th>Телефон</th>
                <th>Стол</th>
                <th>Игра</th>
                <th>Статус</th>
                <th>Источник</th>
                <th style={{ width: 220 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id}>
                  <td>
                    {dateTime(b.startsAt)}
                    <span className="muted"> – {dateTime(b.endsAt).split(', ')[1] ?? ''}</span>
                  </td>
                  <td>{b.customerName}</td>
                  <td className="muted">{b.phone}</td>
                  <td>{tableName(b.tableId)}</td>
                  <td>{kindName(b.gameKind)}</td>
                  <td>
                    <span className="badge">{BOOKING_STATUS_LABEL[b.status] ?? b.status}</span>
                  </td>
                  <td className="muted">{BOOKING_SOURCE_LABEL[b.source] ?? b.source}</td>
                  <td>
                    <div className="row-actions">
                      {b.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void changeStatus(b, 'confirmed')}
                        >
                          Подтвердить
                        </Button>
                      )}
                      {(b.status === 'confirmed' || b.status === 'pending') && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void changeStatus(b, 'no_show')}
                          >
                            Не пришёл
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void changeStatus(b, 'cancelled')}
                          >
                            Отменить
                          </Button>
                        </>
                      )}
                      <IconButton danger title="Удалить" onClick={() => setDeleting(b)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {statusMut.error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {statusMut.error}
        </div>
      )}

      {editing && (
        <BookingForm
          tables={tableList}
          gameTypes={gameTypeList}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить бронь"
          message={`Удалить бронь «${deleting.customerName}» на ${dateTime(deleting.startsAt)}?`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

const DURATIONS = [60, 90, 120, 180, 240]

function BookingForm({
  tables,
  gameTypes,
  query,
  onClose,
  onSaved
}: {
  tables: Table[]
  gameTypes: GameType[]
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [gameKind, setGameKind] = useState<string>('')
  const kindName = (code: string): string => gameTypes.find((g) => g.code === code)?.name ?? code
  const [tableId, setTableId] = useState('')
  const [start, setStart] = useState('')
  const [duration, setDuration] = useState(60)
  const [notes, setNotes] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (name.trim().length < 1) return setValidation('Укажите имя клиента.')
    if (phone.replace(/\D/g, '').length < 5) return setValidation('Укажите телефон.')
    if (!start) return setValidation('Укажите дату и время начала.')
    const startsAt = new Date(start).getTime()
    if (!Number.isFinite(startsAt)) return setValidation('Некорректная дата начала.')
    setValidation(null)

    const body = {
      customerName: name.trim(),
      phone: phone.trim(),
      gameKind: gameKind || undefined,
      tableId: tableId || undefined,
      startsAt,
      endsAt: startsAt + duration * 60000,
      notes: notes.trim() || undefined
    }
    const ok = await save.mutate(() => api.post('/v1/bookings', body, { query }))
    if (ok) onSaved()
  }

  return (
    <Modal
      title="Новая бронь"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="booking-form">
            Создать
          </Button>
        </>
      }
    >
      <form id="booking-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Имя клиента" htmlFor="bk-name" full>
            <Input id="bk-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label="Телефон" htmlFor="bk-phone">
            <Input
              id="bk-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+375…"
            />
          </Field>
          <Field label="Тип игры" htmlFor="bk-kind">
            <Select id="bk-kind" value={gameKind} onChange={(e) => setGameKind(e.target.value)}>
              <option value="">— любой —</option>
              {gameTypes.map((g) => (
                <option key={g.code} value={g.code}>
                  {g.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Начало" htmlFor="bk-start">
            <Input
              id="bk-start"
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </Field>
          <Field label="Длительность" htmlFor="bk-dur">
            <Select
              id="bk-dur"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {DURATIONS.map((m) => (
                <option key={m} value={m}>
                  {m % 60 === 0 ? `${m / 60} ч` : `${m} мин`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Стол (необязательно)" htmlFor="bk-table" full>
            <Select id="bk-table" value={tableId} onChange={(e) => setTableId(e.target.value)}>
              <option value="">— назначить автоматически —</option>
              {tables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.kind ? ` (${kindName(t.kind)})` : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Заметка" htmlFor="bk-notes" full>
            <Input
              id="bk-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="необязательно"
            />
          </Field>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
